import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowLeft, RefreshCw, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { fmtEUR } from "../lib/format";
import { api } from "../lib/api";

/**
 * Callback page reached after the SumUp app returns control.
 * SumUp appends query parameters to the callback URL we provided when starting
 * the payment. Common parameters include:
 *   - smp-status      success | failed | cancelled
 *   - smp-tx-code     transaction code
 *   - foreign-tx-id   our order id
 * Some integrations use snake_case (smp_status). We support both.
 */
export default function KassaSumUpCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("processing"); // processing | success | failed | unknown
  const [order, setOrder] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let alive = true;
    const orderId =
      params.get("foreign-tx-id") ||
      params.get("foreign_tx_id") ||
      params.get("order_id") ||
      JSON.parse(sessionStorage.getItem("teller_sumup_pending") || "{}").order_id;

    const smpStatus =
      params.get("smp-status") ||
      params.get("smp_status") ||
      params.get("status") ||
      "";
    const smpTxCode =
      params.get("smp-tx-code") ||
      params.get("smp_tx_code") ||
      params.get("transaction-code") ||
      "";

    if (!orderId) {
      toast.error("Kan bestelling niet vinden");
      // eslint-disable-next-line
      setState("failed");
      return;
    }

    const run = async () => {
      try {
        const { data } = await api.post("/payments/sumup/appswitch/callback", {
          order_id: orderId,
          smp_status: smpStatus || null,
          smp_tx_code: smpTxCode || null,
          raw_query: window.location.search,
        });
        if (!alive) return;
        setOrder(data.order);
        if (data.order.betaal_status === "betaald") {
          setState("success");
          toast.success(`Order ${data.order.ordernummer} betaald!`, { duration: 3000 });
          sessionStorage.removeItem("teller_sumup_pending");
        } else if (data.order.betaal_status === "mislukt") {
          setState("failed");
          toast.error("Betaling niet gelukt");
        } else {
          setState("unknown");
        }
      } catch (e) {
        if (alive) {
          setState("failed");
          toast.error("Kon callback niet verwerken");
        }
      } finally {
        if (alive) setRefreshing(false);
      }
    };
    run();

    return () => {
      alive = false;
    };
  }, [params, refreshToken]);

  const onRefresh = () => {
    setRefreshing(true);
    setState("processing");
    setRefreshToken((t) => t + 1);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg-soft)] px-4"
      data-testid="sumup-callback-screen"
    >
      <div className="w-full max-w-md tile p-8 text-center">
        {state === "processing" && (
          <div data-testid="sumup-state-processing">
            <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-[var(--ink-soft)]" />
            <div className="text-xl font-extrabold">Betaling verwerken…</div>
            <div className="text-sm text-[var(--ink-soft)] mt-2">
              We controleren de status bij SumUp
            </div>
          </div>
        )}

        {state === "success" && (
          <div data-testid="sumup-state-success">
            <CheckCircle2 className="w-20 h-20 mx-auto mb-4 text-[var(--success)]" />
            <div className="text-3xl font-display font-extrabold text-[var(--success)]">
              Betaald!
            </div>
            {order && (
              <div className="mt-2 text-lg">
                Order{" "}
                <span
                  className="px-2 py-1 rounded-lg text-white font-extrabold"
                  style={{ background: order.team_kleur }}
                >
                  {order.ordernummer}
                </span>{" "}
                · {fmtEUR(order.totaal)}
              </div>
            )}
          </div>
        )}

        {state === "failed" && (
          <div data-testid="sumup-state-failed">
            <XCircle className="w-20 h-20 mx-auto mb-4 text-[var(--danger)]" />
            <div className="text-2xl font-display font-extrabold text-[var(--danger)]">
              Betaling niet gelukt
            </div>
            <div className="text-sm text-[var(--ink-soft)] mt-2">
              Probeer opnieuw of kies een andere betaalmethode
            </div>
          </div>
        )}

        {state === "unknown" && (
          <div data-testid="sumup-state-unknown">
            <Clock3 className="w-20 h-20 mx-auto mb-4 text-[var(--ink-soft)]" />
            <div className="text-2xl font-display font-extrabold">
              Status nog onbekend
            </div>
            <div className="text-sm text-[var(--ink-soft)] mt-2">
              SumUp heeft nog geen definitieve status doorgegeven.
            </div>
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="btn-press inline-flex items-center gap-2 mt-4 px-5 py-3 rounded-2xl bg-[var(--brand-yellow)] text-[var(--ink)] font-bold disabled:opacity-50"
              data-testid="sumup-refresh-status"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Verversen…" : "Status verversen"}
            </button>
          </div>
        )}

        <button
          onClick={() => navigate("/kassa")}
          className="btn-press inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-2xl bg-[var(--ink)] text-white font-bold"
          data-testid="sumup-back-to-kassa"
        >
          <ArrowLeft className="w-4 h-4" /> Terug naar kassa
        </button>
      </div>
    </div>
  );
}
